/**
 * HOW OFTEN A PLAYER'S MIXER IS ADVANCED, BY DISTANCE FROM THE CAMERA.
 *
 * `playerVisual.ts` (authored master) and `castPlayerVisual.ts` (cast) both draw
 * every player every frame and both throttle the ANIMATION underneath. They
 * carried two hand-copied sets of these numbers with a comment on each saying it
 * matched the other; they are here so that stays true by construction, because
 * the near band's value is a defect fix and not a performance knob.
 *
 * ── "HE RANDOMLY JUMPS" (owner, reported three times) ──────────────────────
 *
 * THE NEAR BAND MUST NOT BE THROTTLED, AND THROTTLING IT WAS THE VERTICAL POP.
 *
 * `tickInterp.ts` fixed the ROOT: px/py/pz are lerped between 60 Hz sim ticks,
 * so the body glides. But the body's visible HEIGHT is not the root — it is
 * `py + hipsY(clipTime)`, and `hipsY` only moves when the mixer is advanced.
 * The gaits carry 33-119 mm of peak-to-peak pelvis bob (it is what makes a run
 * look like a run), so sampling the clip at 30 Hz under a 61-165 fps draw loop
 * froze the whole body vertically for most of every group of frames and then
 * discharged the entire accumulated interval in one. A smoothly gliding root
 * carrying a body that hitches straight up and stops is exactly the report.
 *
 * Measured on the shipped `M_*.glb` pack, per RENDER frame, worst case M_Run
 * (118.8 mm peak-to-peak):
 *
 *   render fps   mixer         pelvis frozen on   median step   p99      max
 *      61.3      30 Hz               67 %            0.00 mm   28.38   29.06 mm
 *      61.3      every frame          0 %            4.35 mm   10.20   10.42 mm
 *       165      30 Hz               83 %            0.00 mm   20.72   22.90 mm
 *       165      every frame          0 %            1.59 mm    3.86    4.03 mm
 *
 * A 29 mm pop inside a single frame, on a man whose pelvis rides at ~0.95 m,
 * against a camera that is spring-smoothed on the render clock — that is the
 * jump. It is not the clips (their own curves are smooth) and it is not the
 * camera (it was already per-frame); it is the rate at which the pose is
 * sampled, which is why measuring either of those in isolation cleared them.
 *
 * ⚠ AND IT IS IRREGULAR, WHICH IS THE "RANDOMLY". `1/30` is not a multiple of
 * any real frame time, so the accumulator crosses after a varying number of
 * frames. With ±2 ms of ordinary frame-time jitter, sampled over 20 s: at
 * 61.3 fps the gap is 2 frames 160 times and 3 frames 302 times; at 165 fps it
 * spreads across 5, 6, 7 and 8. The pop lands on no fixed beat, so it reads as
 * random rather than as a steady stutter — and a screenshot, landing seconds
 * apart on a ~20 Hz artefact, can never photograph it.
 *
 * MID and FAR keep their throttle. That is where the saving actually is — a
 * 29 mm pop at 42 m is a fraction of a pixel — and it is what the budget was
 * written for. NEAR is at most the handful of players filling the frame, and
 * they are precisely the ones whose pose stepping is visible.
 */

/** Inside this many metres of the camera, a player animates at the full render rate. */
export const NEAR_M = 16;
/** Beyond `NEAR_M` and inside this, the mid band. Beyond it, the far band. */
export const MID_M = 42;

/**
 * ⚠ `Infinity` ON PURPOSE, AND IT IS LOAD-BEARING. It makes the callers'
 * `accum >= 1 / hz` unconditionally true, so a near player's mixer advances
 * every frame with that frame's exact `dt` and the accumulator degenerates to a
 * no-op. Any finite value at or below the display's refresh rate re-introduces
 * the vertical pop described above.
 */
export const HZ_NEAR = Infinity;
export const HZ_MID = 15;
export const HZ_FAR = 10;

/** 0 near, 1 mid, 2 far. Takes the SQUARED distance, which is what callers have. */
export function bandForDistanceSq(d2: number): 0 | 1 | 2 {
  return d2 < NEAR_M * NEAR_M ? 0 : d2 < MID_M * MID_M ? 1 : 2;
}

/** Mixer update rate for a band, Hz. `Infinity` means "every render frame". */
export function mixerHzForBand(band: 0 | 1 | 2): number {
  return band === 0 ? HZ_NEAR : band === 1 ? HZ_MID : HZ_FAR;
}
